import {
	Component,
	OnInit,
	Optional,
	OnDestroy,
	HostBinding
} from "@angular/core";
import { NotificationService } from "../notifications.service";
import { Notification, Offsets, DockPosition } from "../notification.class";
import { filter, takeUntil } from "rxjs/operators";
import { trigger, transition, style, animate } from "@angular/animations";
import { NotificationConfig } from "../notification.config";
import { Subject } from "rxjs";

@Component({
	selector: "trycatch-notification-container",
	templateUrl: "./notification-container.component.html",
	styleUrls: ["./notification-container.component.scss"],
	animations: [
		trigger("fadeIn", [
			transition(":enter", [
				style({ opacity: "0", height: "0" }),
				animate(
					".25s ease-in-out",
					style({ opacity: "1", height: "*" })
				)
			]),
			transition(":leave", [
				style({ opacity: "1" }),
				animate(
					".35s ease-in-out",
					style({ opacity: "0", height: "0" })
				)
			])
		])
	]
})
export class NotificationContainerComponent implements OnInit, OnDestroy {
	_notifications: Notification[];
	private __config: NotificationConfig;
	private __unsubscribeAll: Subject<any>;
	private __offsets: Offsets;

	@HostBinding("class") class: DockPosition = "bottom-right";

	@HostBinding("style.margin-top.px") marginTop = undefined;
	@HostBinding("style.margin-right.px") marginRight = undefined;
	@HostBinding("style.margin-bottom.px") marginBottom = undefined;
	@HostBinding("style.margin-left.px") marginLeft = undefined;

	constructor(
		private notificationService: NotificationService,
		@Optional() config: NotificationConfig
	) {
		this._notifications = new Array<Notification>();

		if (config) {
			this.__config = config;
		}

		if (this.__config && this.__config.position) {
			this.class = this.__config.position;
		}

		if (this.__config && this.__config.offsets) {
			this.__offsets = this.__config.offsets;
			this.__setMarginsFor(this.class);
		}

		this.__unsubscribeAll = new Subject();
	}

	ngOnInit() {
		this.__subscribeToPositionAdjustment();
		this.__subscribeToRaiseNotification();
		this.__subscribeToClearNotification();
		this.__subscribeToClearTypeNotifications();
	}

	ngOnDestroy() {
		this.__unsubscribeAll.next();
		this.__unsubscribeAll.complete();
	}

	private __subscribeToPositionAdjustment() {
		this.notificationService.positionAdjustment$
			.pipe(
				filter(newPosition => !!newPosition),
				takeUntil(this.__unsubscribeAll)
			)
			.subscribe(newPosition => {
				this.class = newPosition.dockPosition || this.class;
				this.__offsets = newPosition.offsets || this.__offsets;

				console.log("new dock position : ", this.class);
				console.log("new offsets : ", this.__offsets);

				this.__setMarginsFor(this.class);
			});
	}

	private __subscribeToClearTypeNotifications() {
		this.notificationService.clearTypeNotifications$
			.pipe(
				filter(type => !!type),
				takeUntil(this.__unsubscribeAll)
			)
			.subscribe(type => {
				const notificationsToClear =
					type !== "all"
						? this._notifications.filter(n => n.type === type)
						: this._notifications.filter(n => n);
				notificationsToClear.forEach(n => {
					this.__clearNotification(n.id);
				});
			});
	}

	private __subscribeToClearNotification() {
		this.notificationService.clearNotification$
			.pipe(
				filter(id => !!id),
				takeUntil(this.__unsubscribeAll)
			)
			.subscribe(notificationId => {
				this.__clearNotification(notificationId);
			});
	}

	private __subscribeToRaiseNotification() {
		this.notificationService.raiseNotification$
			.pipe(
				filter(notification => !!notification),
				takeUntil(this.__unsubscribeAll)
			)
			.subscribe(notification => {
				this._notifications.push(notification);
			});
	}

	private __setMarginsFor(
		position:
			| "top-right"
			| "bottom-right"
			| "top-left"
			| "bottom-left"
			| "bottom-center"
			| "top-center"
	) {
		switch (position) {
			case "top-right":
				this.marginTop = this.__margin("top");
				this.marginRight = this.__margin("right");
				break;
			case "top-left":
				this.marginTop = this.__margin("top");
				this.marginLeft = this.__margin("left");
				break;
			case "top-center":
				this.marginTop = this.__margin("top");
				break;
			case "bottom-right":
				this.marginBottom = this.__margin("bottom");
				this.marginRight = this.__margin("right");
				break;
			case "bottom-left":
				this.marginBottom = this.__margin("bottom");
				this.marginLeft = this.__margin("left");
				break;
			case "bottom-center":
				this.marginBottom = this.__margin("bottom");
				break;
		}
	}

	private __margin(marginType: "top" | "right" | "bottom" | "left"): number {
		const verticalOffset = this.__offset("vertical");
		const horizontalOffset = this.__offset("horizontal");
		return marginType === "top" || marginType === "bottom"
			? verticalOffset
			: horizontalOffset;
	}

	private __offset(offsetType: "vertical" | "horizontal"): number {
		return this.__offsets
			? offsetType === "vertical"
				? this.__offsets.vertical || 0
				: offsetType === "horizontal"
				? this.__offsets.horizontal || 0
				: 0
			: 0;
	}

	private __clearNotification(notificationId: string) {
		const index = this._notifications
			.map(n => n.id.toString())
			.indexOf(notificationId);
		this._notifications.splice(index, 1);
	}

	_handleNotificationTimeUp(notificationId: string) {
		this.__clearNotification(notificationId);
	}
}
